import { useEffect, useState } from 'react';
import { authenticatedFetch } from '@/lib/authenticated-fetch';

async function errorFromResponse(res: Response): Promise<string> {
  const fallback = 'No se pudo cargar el archivo multimedia.';
  try {
    const text = await res.text();
    if (!text) return fallback;
    const data = JSON.parse(text) as { message?: string | string[] };
    const message = Array.isArray(data.message) ? data.message[0] : data.message;
    return typeof message === 'string' && message.trim() ? message.trim() : fallback;
  } catch {
    return fallback;
  }
}

export function useAuthenticatedBlobUrl(path?: string) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));

  useEffect(() => {
    if (!path) {
      setBlobUrl(null);
      setBlob(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = path.startsWith('/api') ? path : `/api${path}`;
        const res = await authenticatedFetch(url);
        if (!res.ok) {
          throw new Error(await errorFromResponse(res));
        }
        const raw = await res.blob();
        const contentType = res.headers.get('Content-Type')?.split(';')[0]?.trim();
        const typed =
          raw.type && raw.type !== 'application/octet-stream'
            ? raw
            : new Blob([raw], { type: contentType || raw.type || 'application/octet-stream' });
        objectUrl = URL.createObjectURL(typed);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setBlob(typed);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return { blobUrl, blob, error, loading };
}
